import AsyncStorage from '@react-native-async-storage/async-storage';
import { Dimensions, Platform } from 'react-native';
import {
  PILOT_APP_VERSION,
  PILOT_CONSENT_VERSION,
  PILOT_FEATURE_ENABLED,
} from '../config/internalTestConfig';
import { requestPilotErasure, submitPilotEventBatch } from '../api/pilotEvents';
import { clearAuthSession, getStoredAccessToken } from './authSession';
import { resetPrototypeSession } from './prototypeSession';
import { createControlledLogout } from './sessionLogout';
import {
  createPilotEvent,
  type PilotDeviceClass,
  type PilotErrorCategory,
  type PilotEventName,
  type PilotEventStatus,
  type PilotFlowName,
} from './internalTestPolicy';
import { createPilotTelemetryStore } from './pilotTelemetryStore';

export const createPilotEventId = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const value = Math.floor(Math.random() * 16);
    const digit = character === 'x' ? value : (value & 0x3) | 0x8;
    return digit.toString(16);
  });

export const getPilotDeviceClass = (): PilotDeviceClass => {
  if (Platform.OS !== 'android') return 'unknown';
  return Dimensions.get('window').width >= 768 ? 'android_tablet' : 'android_phone';
};

const store = createPilotTelemetryStore({
  storage: AsyncStorage,
  sendBatch: submitPilotEventBatch,
});

export const activatePilotTelemetry = async (
  context: { activatedAt: string; sessionId: string },
  queuedEvents: Parameters<typeof store.activate>[1] = []
) => {
  await store.activate(context, queuedEvents);
  if (queuedEvents.length > 0) void store.flush();
};

const occurredDay = (activatedAt: string | null, at: Date): number => {
  if (!activatedAt) return 0;
  const elapsed = Math.max(0, at.getTime() - new Date(activatedAt).getTime());
  return Math.min(7, Math.floor(elapsed / 86_400_000));
};

export const recordPilotEvent = async ({
  eventName,
  eventStatus,
  flowName,
  errorCategory,
  durationMs,
}: {
  eventName: PilotEventName;
  eventStatus: PilotEventStatus;
  flowName?: PilotFlowName;
  errorCategory?: PilotErrorCategory;
  durationMs?: number;
}): Promise<boolean> => {
  if (!PILOT_FEATURE_ENABLED) return false;
  try {
    const context = await store.getContext();
    const now = new Date();
    const event = createPilotEvent({
      eventId: createPilotEventId(),
      sessionId: context.sessionId ?? undefined,
      eventName,
      eventStatus,
      flowName,
      errorCategory,
      durationMs,
      occurredDay: occurredDay(context.activatedAt, now),
      eventAt: now.toISOString(),
      appVersion: PILOT_APP_VERSION,
      deviceClass: getPilotDeviceClass(),
      consentVersion: PILOT_CONSENT_VERSION,
    });
    const queued = await store.enqueue(event);
    if (queued) void store.flush();
    return queued;
  } catch {
    return false;
  }
};

export const recordPilotFlowStarted = (flowName: PilotFlowName) =>
  recordPilotEvent({
    eventName: `flow_${flowName}_started` as PilotEventName,
    eventStatus: 'started',
    flowName,
  });

export const recordPilotFlowFinished = (
  flowName: PilotFlowName,
  eventStatus: 'passed' | 'failed' | 'skipped',
  options: { errorCategory?: PilotErrorCategory; durationMs?: number } = {}
) =>
  recordPilotEvent({
    eventName: `flow_${flowName}_finished` as PilotEventName,
    eventStatus,
    flowName,
    ...options,
  });

export const clearPilotTelemetry = () => store.clear();

export const withdrawPilotConsentAndLogout = createControlledLogout({
  getPilotContext: store.getContext,
  getStoredAccessToken,
  clearAuthSession,
  resetPrototypeSession,
  requestPilotErasure,
});
